import type { GraphNodeOffset } from '../../../core/entities/graph-document';

export type { GraphNodeOffset };

export interface GraphNodeVelocity {
  x: number;
  y: number;
}

export type GraphNodeOffsetMap = Record<string, GraphNodeOffset>;
export type GraphNodeVelocityMap = Record<string, GraphNodeVelocity>;

export const NODE_MOTION_DECAY = 0.82;
export const NODE_MOTION_STOP_THRESHOLD = 0.08;

export function setNodeVelocity(
  velocities: GraphNodeVelocityMap,
  nodeId: string,
  velocityX: number,
  velocityY: number
): GraphNodeVelocityMap {
  return {
    ...velocities,
    [nodeId]: { x: velocityX, y: velocityY },
  };
}

export function clearNodeVelocity(velocities: GraphNodeVelocityMap, nodeId: string): GraphNodeVelocityMap {
  if (!(nodeId in velocities)) {
    return velocities;
  }

  const next = { ...velocities };
  delete next[nodeId];
  return next;
}

export function hasNodeMotion(velocities: GraphNodeVelocityMap): boolean {
  return Object.keys(velocities).length > 0;
}

export function advanceNodeMotion(
  offsets: GraphNodeOffsetMap,
  velocities: GraphNodeVelocityMap,
  decay = NODE_MOTION_DECAY,
  stopThreshold = NODE_MOTION_STOP_THRESHOLD
): { offsets: GraphNodeOffsetMap; velocities: GraphNodeVelocityMap } {
  const nextOffsets = { ...offsets };
  const nextVelocities: GraphNodeVelocityMap = {};

  for (const [nodeId, velocity] of Object.entries(velocities)) {
    const nextVelocity = {
      x: velocity.x * decay,
      y: velocity.y * decay,
    };

    nextOffsets[nodeId] = {
      x: (nextOffsets[nodeId]?.x ?? 0) + velocity.x,
      y: (nextOffsets[nodeId]?.y ?? 0) + velocity.y,
    };

    if (Math.abs(nextVelocity.x) >= stopThreshold || Math.abs(nextVelocity.y) >= stopThreshold) {
      nextVelocities[nodeId] = nextVelocity;
    }
  }

  return {
    offsets: nextOffsets,
    velocities: nextVelocities,
  };
}
